import React from 'react';
import { VideoJSPlayer } from '../VideoPlayer';
import styles from './VideoDetailPlayerSection.module.css';
import { VideoDetailPlayerSectionProps } from './types';
import { useVideoDetailPlayerSection } from './useVideoDetailPlayerSection';

/**
 * 動画プレイヤー部分を切り出したコンポーネント
 * VideoJSプレイヤーを使用
 */
const VideoDetailPlayerSection: React.FC<VideoDetailPlayerSectionProps> = (props) => {
  const {
    videoId,
    videoPath,
    useStreaming = true,
    initialPosition = 0,
    playerRef,
    onReady,
    onDuration,
    onProgress,
    onEnded,
    autoPlay = true,
  } = props;

  const {
    error,
    videoUrl,
    handleReady,
    handleError,
  } = useVideoDetailPlayerSection({
    ...props,
    videoPath
  });

  // VideoJS用のprops変換とReactPlayer互換性確保
  const videoJSProps = {
    videoId: videoId || 'default',
    videoUrl: videoUrl || videoPath || '', // videoUrlが空の場合はvideoPathを使用
    initialTime: initialPosition,
    autoPlay,
    muted: false, // 初期はミュートなし（VideoJSが自動で制御）
    useStreaming,
    onReady: (player: any) => {
      // ReactPlayer互換のプレイヤーオブジェクトを作成
      const reactPlayerCompatible = {
        // 基本制御
        play: () => {
          try {
            return player.play ? player.play() : Promise.resolve();
          } catch (error) {
            console.warn('play error:', error);
            return Promise.resolve();
          }
        },
        pause: () => {
          try {
            if (player.pause) {
              player.pause();
            }
          } catch (error) {
            console.warn('pause error:', error);
          }
        },
        seekTo: (time: number) => {
          try {
            if (player.currentTime) {
              player.currentTime(time);
            }
          } catch (error) {
            console.warn('seekTo error:', error);
          }
        },
        getCurrentTime: () => {
          try {
            const currentTime = player.currentTime ? player.currentTime() : 0;
            return currentTime || 0;
          } catch (error) {
            console.warn('getCurrentTime error:', error);
            return 0;
          }
        },
        getDuration: () => {
          try {
            // VideoJSプレイヤーでは duration() メソッドを使用
            const duration = player.duration ? player.duration() : 0;
            return duration || 0;
          } catch (error) {
            console.warn('getDuration error:', error);
            return 0;
          }
        },
        
        // 音量制御
        setVolume: (volume: number) => {
          try {
            if (player.volume) {
              player.volume(Math.max(0, Math.min(1, volume)));
            }
          } catch (error) {
            console.warn('setVolume error:', error);
          }
        },
        getVolume: () => {
          try {
            return player.volume ? player.volume() : 1;
          } catch (error) {
            console.warn('getVolume error:', error);
            return 1;
          }
        },
        setMuted: (muted: boolean) => {
          try {
            if (player.muted) {
              player.muted(muted);
            }
          } catch (error) {
            console.warn('setMuted error:', error);
          }
        },
        getMuted: () => {
          try {
            return player.muted ? player.muted() : false;
          } catch (error) {
            console.warn('getMuted error:', error);
            return false;
          }
        },
        
        // 再生速度
        setPlaybackRate: (rate: number) => {
          try {
            if (player.playbackRate) {
              player.playbackRate(rate);
            }
          } catch (error) {
            console.warn('setPlaybackRate error:', error);
          }
        },
        getPlaybackRate: () => {
          try {
            return player.playbackRate ? player.playbackRate() : 1;
          } catch (error) {
            console.warn('getPlaybackRate error:', error);
            return 1;
          }
        },
        
        // 状態取得
        isPlaying: () => {
          try {
            return player.paused ? !player.paused() : false;
          } catch (error) {
            console.warn('isPlaying error:', error);
            return false;
          }
        },
        isPaused: () => {
          try {
            return player.paused ? player.paused() : true;
          } catch (error) {
            console.warn('isPaused error:', error);
            return true;
          }
        },
        isEnded: () => {
          try {
            return player.ended ? player.ended() : false;
          } catch (error) {
            console.warn('isEnded error:', error);
            return false;
          }
        },
        
        // 10秒送り・戻し機能
        seekForward10: () => player.getPlaybackControls?.()?.seekForward10?.(),
        seekBackward10: () => player.getPlaybackControls?.()?.seekBackward10?.(),
        
        // 内部プレイヤー取得: VideoJSPlayer互換として vjsPlayer を返す
        getInternalPlayer: () => ({ vjsPlayer: player }),
        
        // バッファ情報（ReactPlayer互換）
        getSecondsLoaded: () => {
          const internalPlayer = player.getInternalPlayer();
          if (internalPlayer && internalPlayer.vjsPlayer && internalPlayer.vjsPlayer.buffered) {
            const buffered = internalPlayer.vjsPlayer.buffered();
            return buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;
          }
          return 0;
        },
      };
      
      // playerRefに代入
      if (playerRef && typeof playerRef === 'object' && 'current' in playerRef) {
        playerRef.current = reactPlayerCompatible as any;
        
        // テスト: getDurationメソッドを即座に呼び出してみる
        try {
          const testDuration = reactPlayerCompatible.getDuration();
        } catch (error) {
          console.warn('VideoDetailPlayerSection: Test call to getDuration() failed:', error);
        }
      } else {
        console.warn('VideoDetailPlayerSection: playerRef is not valid:', playerRef);
      }
      
      // onReadyコールバック実行
      if (onReady) {
        onReady(reactPlayerCompatible as any);
      }
      
      // 既存のhandleReady呼び出し
      handleReady(reactPlayerCompatible as any);
    },
    
    onProgress: (data: any) => {
      // ReactPlayer互換の形式に変換
      if (onProgress) {
        const currentTime = data.currentTime || 0;
        const duration = data.duration || 1;
        const buffered = data.buffered || 0;
        
        onProgress({
          played: duration > 0 ? currentTime / duration : 0,
          playedSeconds: currentTime,
          loaded: duration > 0 ? buffered / duration : 0,
          loadedSeconds: buffered
        });
      }
    },
    
    onDuration: (duration: number) => {
      if (onDuration) {
        onDuration(duration);
      } else {
        console.warn('VideoDetailPlayerSection: onDuration prop is undefined, cannot call parent');
      }
    },
    
    onEnded: () => {
      if (onEnded) {
        onEnded();
      }
    },
    
    onError: (error: any) => {
      console.error('VideoJSプレイヤーエラー:', error);
      handleError(error);
    }
  };

  // エラー状態の場合
  if (error) {
    return (
      <div className={styles["video-detail-player-section__container"]}>
        <div className={styles["video-detail-player-section__error"]}>
          <h3>動画の読み込みに失敗しました</h3>
          <p>{error.toString()}</p>
          <p>ページを再読み込みするか、管理者にお問い合わせください。</p>
        </div>
      </div>
    );
  }

  // 動画URL読み込み中の場合でも、VideoJSPlayerに委譲する
  return (
    <div className={styles["video-detail-player-section__container"]}>
      <VideoJSPlayer
        {...videoJSProps}
        className={styles["video-detail-player-section__player"]}
      />
    </div>
  );
};

export default VideoDetailPlayerSection; 